import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Inbox, Mail, FileText, Briefcase, DollarSign, TrendingUp, Loader2, DatabaseBackup } from "lucide-react";

const cardMeta = [
  { key: "revenue", label: "Pipeline Value", icon: DollarSign, prefix: "$", accent: "text-emerald-400" },
  { key: "leads_total", label: "Total Leads", icon: Inbox, accent: "text-vantage" },
  { key: "subscribers", label: "Subscribers", icon: Mail, accent: "text-purple-400" },
  { key: "conversion", label: "Conversion", icon: TrendingUp, suffix: "%", accent: "text-amber-400" },
];

const secondary = [
  { key: "blog_posts", label: "Blog Posts", icon: FileText },
  { key: "projects", label: "Projects", icon: Briefcase },
  { key: "media", label: "Media Files", icon: FileText },
  { key: "leads_new", label: "New Leads", icon: Inbox },
];

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-ink-800 px-3 py-2 text-xs shadow-xl">
      <p className="mb-1 text-slate-400">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="font-medium text-white">{p.name}: {p.value.toLocaleString()}</p>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/data/leads").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  if (!data) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-vantage" /></div>;

  const c = data.cards;
  const fmt = (m) => `${m.prefix || ""}${(c[m.key] ?? 0).toLocaleString()}${m.suffix || ""}`;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-400">Your business at a glance.</p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-ink-900 px-4 py-2 text-xs text-slate-400" data-testid="last-backup">
          <DatabaseBackup className="h-4 w-4 text-emerald-400" />
          Last backup: {data.last_backup ? (data.last_backup || "").slice(0, 16).replace("T", " ") : "pending"}
        </div>
      </div>

      {/* primary cards */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cardMeta.map((m) => (
          <div key={m.key} className="rounded-2xl border border-white/10 bg-ink-900 p-6" data-testid={`stat-${m.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">{m.label}</span>
              <m.icon className={`h-5 w-5 ${m.accent}`} />
            </div>
            <p className="mt-4 font-heading text-3xl font-bold text-white">{fmt(m)}</p>
          </div>
        ))}
      </div>

      {/* charts */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-ink-900 p-6 lg:col-span-2">
          <h3 className="font-heading text-lg font-semibold text-white">Traffic & Leads (7 days)</h3>
          <div className="mt-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.traffic}>
                <defs>
                  <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0066FF" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#0066FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<TooltipBox />} />
                <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#0066FF" strokeWidth={2} fill="url(#gVisitors)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-ink-900 p-6">
          <h3 className="font-heading text-lg font-semibold text-white">Leads by Service</h3>
          <div className="mt-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.leads_by_service} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={11} width={90} tickLine={false} axisLine={false} />
                <Tooltip content={<TooltipBox />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="value" name="Leads" fill="#0066FF" radius={[0, 6, 6, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* secondary + recent */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="grid grid-cols-2 gap-5 lg:col-span-1">
          {secondary.map((m) => (
            <div key={m.key} className="rounded-2xl border border-white/10 bg-ink-900 p-5">
              <m.icon className="h-5 w-5 text-slate-500" />
              <p className="mt-3 font-heading text-2xl font-bold text-white">{(c[m.key] ?? 0).toLocaleString()}</p>
              <p className="text-xs text-slate-400">{m.label}</p>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-white/10 bg-ink-900 p-6 lg:col-span-2">
          <h3 className="font-heading text-lg font-semibold text-white">Recent Leads</h3>
          <div className="mt-4 divide-y divide-white/5">
            {recent.length === 0 && <p className="py-6 text-sm text-slate-500">No leads yet.</p>}
            {recent.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-white">{l.name}</p>
                  <p className="text-xs text-slate-500">{l.email} · {l.service || "General"}</p>
                </div>
                <span className="rounded-full bg-vantage/10 px-2.5 py-1 text-xs text-vantage">{l.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
